import clsx from 'clsx';
import * as React from 'react';
import { tv, VariantProps } from 'tailwind-variants';
import * as Tooltip from '@radix-ui/react-tooltip';

export const buttonDivVariants = tv({
  base: 'flex items-center justify-center w-fit transition-colors duration-300 cursor-pointer',
  variants: {
    variant: {
      primary: 'text-content-lighter bg-surface-interactive border border-transparent',
      secondary: 'text-content-dark bg-surface-white border border-stroke-light',
      tertiary: 'text-content-interactive bg-transparent border border-transparent',
      destructive: 'text-danger-dark bg-transparent border border-transparent',
    },
    size: {
      sm: 'rounded-radius-sm px-sm py-xs !text-label-small gap-xs',
      lg: 'rounded-radius-md px-lg py-sm !text-label-large gap-sm',
      'icon-sm': 'rounded-radius-sm p-xs !text-sm gap-xs',
      'icon-lg': 'rounded-radius-md p-sm !text-lg gap-sm',
    },
    disabled: {
      true: 'opacity-50 hover:cursor-not-allowed',
      false: '',
    },
    selected: {
      true: '',
      false: '',
    },
  },

  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },

  compoundVariants: [
    // Hover State Variants
    {
      variant: 'primary',
      disabled: false,
      className: 'hover:bg-surface-interactive-dark',
    },
    {
      variant: 'secondary',
      disabled: false,
      className: 'hover:bg-surface-interactive-lighter hover:border-stroke-interactive hover:text-content-interactive',
    },
    {
      variant: 'tertiary',
      disabled: false,
      className: 'hover:text-content-interactive hover:bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      disabled: false,
      className: 'hover:bg-danger-light',
    },

    // Selected State Variants
    {
      variant: 'primary',
      selected: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'tertiary',
      selected: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      className: 'bg-danger-light',
    },

    //selected and disabled state variants
    {
      variant: 'primary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'tertiary',
      selected: true,
      disabled: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      disabled: true,
      className: 'bg-danger-light',
    },
  ],
});

export interface ButtonDivProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonDivVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /**
   * When set, the button is rendered as disabled and wrapped in a Tooltip
   * with this string as content.
   */
  disabledReason?: string;
}

const ButtonDiv = React.forwardRef<HTMLDivElement, ButtonDivProps>(
  (
    {
      className,
      leftIcon,
      rightIcon,
      children,
      variant,
      size,
      disabled = false,
      selected = false,
      onClick,
      onKeyDown,
      tabIndex,
      disabledReason,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || !!disabledReason;
    const isRoleButton = rest.role === 'button';
    const variants = React.useMemo(
      () => buttonDivVariants({ variant, size, disabled: isDisabled, selected }),
      [variant, size, isDisabled, selected],
    );

    // A div with role="button" must be focusable and activate on Enter/Space like a native button
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e);
        if (!isRoleButton || isDisabled || e.defaultPrevented || (e.key !== 'Enter' && e.key !== ' ')) {
          return;
        }
        e.preventDefault();
        e.currentTarget.click();
      },
      [onKeyDown, isRoleButton, isDisabled],
    );

    const buttonElement = (
      <div
        ref={ref}
        className={clsx(className, variants, {
          'focus-visible:ring-2 focus-visible:ring-stroke-interactive focus-visible:outline-none': isRoleButton,
        })}
        onClick={isDisabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        // Disabled stays focusable so the disabledReason tooltip is reachable by keyboard
        tabIndex={tabIndex ?? (isRoleButton ? 0 : undefined)}
        aria-disabled={isRoleButton && isDisabled ? true : undefined}
        {...rest}>
        {leftIcon && <span>{leftIcon}</span>}
        {children}
        {rightIcon && <span>{rightIcon}</span>}
      </div>
    );

    if (disabledReason) {
      return (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{buttonElement}</Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-sm py-xs rounded-radius-sm bg-surface-darker text-white text-body-small shadow-raised z-50"
                sideOffset={4}>
                {disabledReason}
                <Tooltip.Arrow className="fill-surface-darker" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return buttonElement;
  },
);

ButtonDiv.displayName = 'ButtonDiv';

export { ButtonDiv };
